import toast from 'react-hot-toast';
import { useCallback } from 'react';
import { useErrorModal } from '../contexts/ErrorContext';

interface ErrorResponse {
  success: boolean;
  status: number;
  message: string;
  traceId?: string;
  timestamp?: string;
  details?: any;
}

export const useToast = () => {
  const { showErrorModal } = useErrorModal();

  const showSuccess = useCallback((message: string) => {
    toast.success(message, {
      duration: 4000,
      position: 'top-center',
      style: {
        background: '#10b981',
        color: '#fff',
        fontWeight: '500',
        padding: '16px',
        borderRadius: '8px',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#10b981',
      },
    });
  }, []);

  const showError = useCallback((error: any, defaultMessage: string = 'Ocurrió un error') => {
    let errorMessage = defaultMessage;
    let traceId: string | undefined;
    let statusCode: number | undefined;

    // Check if it's our custom ApiError with details
    if (error?.details) {
      const errorData = error.details as ErrorResponse;
      statusCode = errorData.status || error.statusCode;
      
      if (errorData.traceId) {
        traceId = errorData.traceId;
      }

      // Determine user-friendly message based on status code
      if (statusCode === 400 || statusCode === 422) {
        // Validation or bad request errors - show generic message
        errorMessage = 'Los datos proporcionados no son válidos. Por favor, verifica la información e intenta nuevamente.';
      } else if (statusCode && statusCode >= 500) {
        // Server errors - show generic message
        errorMessage = 'Ocurrió un error en el servidor. Por favor, contacta a soporte técnico con el ID de seguimiento.';
      } else if (errorData.message) {
        // For other errors (like 404, 403), show the actual message as it's usually user-friendly
        errorMessage = errorData.message;
      }
    } 
    // Fallback: Check if it's a direct Axios error response
    else if (error?.response?.data) {
      const errorData = error.response.data as ErrorResponse;
      statusCode = errorData.status || error?.response?.status;
      
      if (errorData.traceId) {
        traceId = errorData.traceId;
      }

      // Determine user-friendly message based on status code
      if (statusCode === 400 || statusCode === 422) {
        errorMessage = 'Los datos proporcionados no son válidos. Por favor, verifica la información e intenta nuevamente.';
      } else if (statusCode && statusCode >= 500) {
        errorMessage = 'Ocurrió un error en el servidor. Por favor, contacta a soporte técnico con el ID de seguimiento.';
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } 
    // Fallback: Use error message
    else if (error?.message) {
      errorMessage = error.message;
    }

    // Show error modal
    showErrorModal('Error', errorMessage, traceId);
  }, [showErrorModal]);

  const showLoading = useCallback((message: string) => {
    return toast.loading(message, {
      position: 'top-right',
    });
  }, []);

  const dismissToast = useCallback((toastId: string) => {
    toast.dismiss(toastId);
  }, []);

  return {
    showSuccess,
    showError,
    showLoading,
    dismissToast,
  };
};
